import { Play, Video } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useVideoConversion } from "@/components/media-viewer/hooks/useVideoConversion";

interface VideoThumbnailProps {
  attachmentId?: number;
  className?: string;
  extension?: string;
  fill?: boolean;
  src: string;
  thumbnailUrl?: null | string;
}

export function VideoThumbnail({
  attachmentId = 0,
  className = "",
  extension = "",
  fill = false,
  src,
  thumbnailUrl,
}: VideoThumbnailProps) {
  const [thumbnailSrc, setThumbnailSrc] = useState<null | string>(null);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const needsConversion = extension.toLowerCase() === "mov";
  const { convertedUrl } = useVideoConversion(attachmentId, needsConversion);
  const effectiveSrc = needsConversion ? convertedUrl : src;

  useEffect(() => {
    setHasError(false);
    if (thumbnailUrl || !effectiveSrc) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    setThumbnailSrc(null);

    const handleSeeked = () => {
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        setHasError(true);

        return;
      }

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      try {
        setThumbnailSrc(canvas.toDataURL());
      } catch {
        setHasError(true);
      }
    };

    const handleLoadedMetadata = () => {
      video.currentTime = 0.001;
    };

    const handleError = () => setHasError(true);

    video.addEventListener("loadedmetadata", handleLoadedMetadata, {
      once: true,
    });
    video.addEventListener("seeked", handleSeeked, { once: true });
    video.addEventListener("error", handleError, { once: true });

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("error", handleError);
    };
  }, [effectiveSrc, thumbnailUrl]);

  const resolvedThumbnail = thumbnailUrl ?? thumbnailSrc;
  const showThumbnail = resolvedThumbnail !== null && !hasError;

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {!thumbnailUrl && effectiveSrc && (
        <>
          <video
            className="hidden"
            crossOrigin="anonymous"
            muted
            preload="metadata"
            ref={videoRef}
            src={effectiveSrc}
          />
          <canvas className="hidden" ref={canvasRef} />
        </>
      )}

      {showThumbnail ? (
        <>
          <img
            alt=""
            className={fill ? "size-full object-cover" : "block h-auto w-full"}
            src={resolvedThumbnail!}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-black/50 p-3">
              <Play className="h-5 w-5 text-white" fill="white" />
            </div>
          </div>
        </>
      ) : (
        <div
          aria-hidden="true"
          className={`flex flex-col items-center justify-center gap-2 bg-[#0C111D] ${
            fill ? "size-full" : "aspect-[14/9] w-full"
          }`}
        >
          <Video className="h-8 w-8 text-white/40" />
          <div className="rounded-full bg-black/50 p-3">
            <Play className="h-5 w-5 text-white" fill="white" />
          </div>
        </div>
      )}
    </div>
  );
}
